import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WarningCircle } from '@phosphor-icons/react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { RETRYABLE_THOUGHT_ERRORS, type IdeaDto, type PublicationKind, type ThoughtDto } from '@app/shared';
import { IdeaCard } from '../components/IdeaCard';
import { StepLayout } from '../components/shell/StepLayout';
import { Button, Card, Skeleton, SkeletonGroup, Spinner } from '../components/ui';
import { useToast } from '../hooks/useToast';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { ApiError } from '../lib/api-core';
import { busyInterval } from '../lib/busy';
import { errorText } from '../lib/errors';
import { stepBack } from '../lib/layout';
import { examplesToWarm } from '../lib/idea-kinds';
import { renderApi } from '../lib/render-client';
import { groupByRound, roundOutcome } from '../lib/thought-view';
import { warmUp } from '../lib/warm';

export function ThoughtPage() {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toast, show } = useToast();
  const id = Number(useParams().id);
  const key = ['thought', id];

  const query = useQuery<ThoughtDto>({
    queryKey: key,
    queryFn: () => api.getThought(id),
    enabled: Number.isInteger(id) && id > 0,
    refetchInterval: (q) => busyInterval('thought', q.state.data?.status),
  });
  const thought = query.data;

  // While the person reads the ideas, the examples of the advised formats come into the cache:
  // «Взять» opens «Формат и текст» with its picture and demo already there.
  const examples = useQuery({
    queryKey: ['render-examples'],
    queryFn: () => renderApi.getExamples(),
    enabled: (thought?.ideas.length ?? 0) > 0,
    staleTime: 10 * 60_000,
  });
  useEffect(() => {
    if (thought && examples.data) warmUp(examplesToWarm(thought.ideas, thought.kinds, examples.data));
  }, [thought, examples.data]);

  const [expanded, setExpanded] = useState(false);
  const [asking, setAsking] = useState(false);
  const [taking, setTaking] = useState<number | null>(null);
  /** Ideas whose kind the passport stopped offering: their cards go back to «Сцены». */
  const [unavailable, setUnavailable] = useState<ReadonlySet<number>>(new Set());
  const roundRefs = useRef(new Map<number, HTMLElement>());

  // A new round: the balance changed (the first round and paid rounds), show the round.
  const prev = useRef<{ status: string; ideaRounds: number } | undefined>(undefined);
  useEffect(() => {
    if (!thought) return;
    const outcome = roundOutcome(prev.current, thought);
    prev.current = { status: thought.status, ideaRounds: thought.ideaRounds };
    if (outcome === 'new_round') {
      void qc.invalidateQueries({ queryKey: ['profile'] });
      void qc.invalidateQueries({ queryKey: ['thoughts'] });
      if (thought.ideaRounds >= 2) {
        const round = thought.ideaRounds;
        requestAnimationFrame(() =>
          roundRefs.current.get(round)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        );
      }
    }
    if (outcome === 'failed') show(t.ideas.moreFailed, 'warning');
  }, [thought, qc, show, t]);

  const more = async () => {
    setAsking(true);
    try {
      qc.setQueryData(key, await api.moreIdeas(id));
    } catch (err) {
      show(errorText(err, t), 'destructive');
    } finally {
      setAsking(false);
    }
  };

  const take = async (idea: IdeaDto, kind: PublicationKind) => {
    if (idea.draftId !== null) {
      navigate(`/drafts/${idea.draftId}`);
      return;
    }
    setTaking(idea.id);
    try {
      const draft = await api.takeIdea(idea.id, kind);
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['drafts'] });
      navigate(`/drafts/${draft.id}`);
    } catch (err) {
      // The advised format went away from the passport: the card advises «Сцены» (spec 3).
      if (err instanceof ApiError && err.code === 'KIND_UNAVAILABLE') {
        setUnavailable((s) => new Set(s).add(idea.id));
        void qc.invalidateQueries({ queryKey: key });
      }
      show(errorText(err, t), 'destructive');
      setTaking(null);
    }
  };

  const retry = async () => {
    setAsking(true);
    try {
      qc.setQueryData(key, await api.retryThought(id));
    } catch (err) {
      show(errorText(err, t), 'destructive');
    } finally {
      setAsking(false);
    }
  };

  const toHome = (
    <Link to="/" className="inline-block mt-4 text-sm font-semibold text-fg">
      {t.thought.toHome}
    </Link>
  );

  const heard = thought?.transcript ? (
    <Card variant="sunken" padding="md" className="mb-4">
      <h2 className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-1">{t.thought.heard}</h2>
      <p className={expanded ? 'text-sm whitespace-pre-line break-words' : 'text-sm line-clamp-3 break-words'}>
        {thought.transcript}
      </p>
      {!expanded && (
        <button type="button" className="text-xs font-semibold text-fg mt-1" onClick={() => setExpanded(true)}>
          {t.thought.showAll}
        </button>
      )}
    </Card>
  ) : null;

  // Idea cards are coming: bones in their shape (spec 4.5), one calm shimmer.
  const ideaBones = (
    <SkeletonGroup label={t.common.loading} className="space-y-3">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-40" />
      ))}
    </SkeletonGroup>
  );
  const statusLine = (text: string) => (
    <p className="flex items-center gap-2 text-sm text-hint mb-3">
      <Spinner size="sm" />
      {text}
    </p>
  );

  let body: JSX.Element;
  if (query.isError) {
    body = (
      <Card variant="surface" padding="lg" className="text-center">
        <WarningCircle size={24} className="mx-auto mb-2 text-destructive" />
        <p className="text-sm break-words">{errorText(query.error, t)}</p>
        {toHome}
      </Card>
    );
  } else if (!thought) {
    body = ideaBones;
  } else if (thought.status === 'failed') {
    // A failure of the service, not of the recording: the thought can go again (the transcript is kept).
    const retryable = RETRYABLE_THOUGHT_ERRORS.some((e) => e === thought.error);
    body = (
      <>
        <Card variant="surface" padding="lg" className="text-center mb-4">
          <WarningCircle size={24} className="mx-auto mb-2 text-destructive" />
          <p className="text-sm break-words">{thought.error ? t.thought.errors[thought.error] : t.common.failed}</p>
          {retryable && (
            <Button variant="ink" className="mt-4" loading={asking} onClick={retry}>
              {t.thought.retry}
            </Button>
          )}
          <div>{toHome}</div>
        </Card>
        {heard}
      </>
    );
  } else if (thought.status === 'transcribing') {
    body = (
      <>
        {statusLine(t.thought.transcribing)}
        {ideaBones}
      </>
    );
  } else {
    const rounds = groupByRound(thought.ideas);
    const busy = thought.status === 'ideating';
    body = (
      <>
        {heard}

        {rounds.length === 0 && busy && (
          <>
            {statusLine(t.thought.ideating)}
            {ideaBones}
          </>
        )}

        {rounds.map((r) => (
          <section
            key={r.round}
            className="mb-4 scroll-mt-4"
            ref={(el) => {
              if (el) roundRefs.current.set(r.round, el);
              else roundRefs.current.delete(r.round);
            }}
          >
            {r.round >= 2 && (
              <h2 className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2">{t.ideas.moreRound}</h2>
            )}
            <div className="space-y-3">
              {r.ideas.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  kinds={query.data?.kinds ?? []}
                  fallback={unavailable.has(idea.id)}
                  onTake={take}
                  taking={taking === idea.id}
                  disabled={taking !== null && taking !== idea.id}
                />
              ))}
            </div>
          </section>
        ))}

        {rounds.length > 0 && busy && <div className="mb-4">{statusLine(t.thought.ideating)}</div>}

        {rounds.length > 0 &&
          (thought.canMoreIdeas ? (
            <Button variant="secondary" size="md" fullWidth loading={asking || busy} onClick={more}>
              {t.ideas.more}
            </Button>
          ) : (
            <p className="text-sm text-hint text-center">{t.ideas.noMore}</p>
          ))}
      </>
    );
  }

  return (
    <StepLayout title={thought?.ideas.length ? t.thought.pickTitle : t.thought.title} back={stepBack(pathname, {})}>
      {body}
      {toast}
    </StepLayout>
  );
}
